import React, { useRef, useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./styles.scss";
import InsertHtmlModal from "./insert-html";

const QuillToolbar = ({ handleUndo, handleRedo, handleImage, handleInsertHtml }) => (
  <div id="toolbar">
    <span className="ql-formats">
      <button className="ql-undo" onClick={handleUndo} aria-label="Undo">
        &#8618;
      </button>
      <button className="ql-redo" onClick={handleRedo} aria-label="Redo">
        &#8617;
      </button>
    </span>
    <span className="ql-formats">
      <select className="ql-header" defaultValue="3">
        <option value="1">Heading</option>
        <option value="2">Subheading</option>
        <option value="3">Normal</option>
      </select>
    </span>
    <span className="ql-formats">
      <button className="ql-bold" />
      <button className="ql-italic" />
      <button className="ql-underline" />
    </span>
    <span className="ql-formats">
      <select className="ql-align" />
      <select className="ql-color" />
      <select className="ql-background" />
    </span>
    <span className="ql-formats">
      <button className="ql-blockquote" />
      <button className="ql-link" />
      <button onClick={handleImage}>
        <svg viewBox="0 0 18 18">
          <rect className="ql-stroke" height="10" width="12" x="3" y="4"></rect>
          <circle className="ql-fill" cx="6" cy="7" r="1"></circle>
          <polyline
            className="ql-even ql-fill"
            points="5 12 5 11 7 9 8 10 11 7 13 9 13 12 5 12"
          ></polyline>
        </svg>
      </button>
    </span>
    <span className="ql-formats">
      <button className="ql-list" value="ordered" />
      <button className="ql-list" value="bullet" />
    </span>
    <span className="ql-formats">
      <span className="ql-insert-html" onClick={handleInsertHtml}>
        Insert HTML
      </span>
    </span>
  </div>
);

const DocEditor = ({ value, setValue }) => {
  const editorRef = useRef(null);
  const [insertHtmlModalPreview, setInsertHtmlModalPreview] = useState(false);

  const modules = {
    history: {
      delay: 200,
      maxStack: 500,
      userOnly: false,
    },
    toolbar: {
      container: "#toolbar",
    },
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "code-block",
    "list",
    "align",
    "color",
    "background",
    "script",
    "indent",
    "link",
    "image",
    "video",
    "formula",
    "direction",
    "size",
    "font",
  ];

  // HANDLERS
  const setValueHandler = (value, _delta, source) => {
    if (source === "user" || source === "api") setValue(value);
  };
  const handleUndo = () => editorRef.current?.getEditor().history.undo();
  const handleRedo = () => editorRef.current?.getEditor().history.redo();

  const handleInsertHtml = () => setInsertHtmlModalPreview((prev) => !prev);

  const handleImageUpload = () => {
    const editor = editorRef.current?.getEditor();
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = () => {
      const file = input.files[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) return alert("Please select an image.");

      const reader = new FileReader();
      reader.onload = () => {
        const range = editor.getSelection(true);
        editor.insertEmbed(range ? range.index : 0, "image", reader.result);
        if (range) editor.setSelection(range.index + 1);
      };
      reader.readAsDataURL(file);
    };
  };

  const handleInsertHtmlSubmit = (value) => {
    const editor = editorRef.current?.getEditor();
    editor.clipboard.dangerouslyPasteHTML(value.html);
    setInsertHtmlModalPreview(false);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        handleUndo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.shiftKey && e.key === "z"))) {
        e.preventDefault();
        handleRedo();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div className="document-editor">
      <QuillToolbar
        handleUndo={handleUndo}
        handleRedo={handleRedo}
        handleImage={handleImageUpload}
        handleInsertHtml={handleInsertHtml}
      />

      <ReactQuill
        ref={editorRef}
        theme="snow"
        value={value}
        placeholder="Enter blog content"
        onChange={setValueHandler}
        modules={modules}
        formats={formats}
      />

      <InsertHtmlModal
        preview={insertHtmlModalPreview}
        previewHandler={handleInsertHtml}
        submitHandler={handleInsertHtmlSubmit}
      />
    </div>
  );
};

export default DocEditor;
